/**
 * nelnela — Mantine theme tokens v2: "blue hour, lit heroes"
 * From the theme-lab pass, 2026-08-31. Canvas refs: `nelnela Theme Lab.dc.html`
 * (2b lit heroes = the locked recipe · 4a/4b/4c the assembled pair · 4d the diff).
 * Adopted 2026-08-31 (M10-α). v1 stays at docs/design/design_handoff_nelnela_mobile/theme.ts
 * for rollback; this file's design source is docs/design/export_m10a/theme-v2.ts.
 *
 * Decisions baked in (turn-by-turn with Jeff):
 *   neutrals  = cool, TINTED (oklch hue ≈262, chroma ≈.02) — no more untinted gray
 *   accents   = TWO, with strict roles:
 *                 ember #cd7a4d = the learner acting (actions, active nav, due, judgment)
 *                 sky   #4f8fd9 = the app speaking (audio controls, ვ‑ forms mark,
 *                                 sort/search affordances, links, info chips)
 *               sky NEVER touches reading-surface marks (spec §2 — marks stay gray)
 *   depth     = light behaves like light:
 *                 --nn-wash  faint radial top-light on screen bgs
 *                 --nn-well  inset input fields
 *                 --nn-hero  lit gradient + brighter top edge — HERO SURFACES ONLY
 *                 halos on the active tab (ember) — never on content
 *   GRADIENT POLICY: hero surfaces (session card, list card, gloss sheet) and the
 *   primary button ONLY. Chips, rows, nav, secondary buttons: NEVER.
 *   primary button pair (see 4a/4c):
 *     dark  = translucent lit fill rgba(200,215,245,.17→.09) + lit top edge + shadow
 *     light = SOLID deep-slate gradient #33425f→#273349, white text — emphasis must
 *             read the same weight in both schemes (never a gray wash on white)
 *   errors    = unchanged coral tints; ember is never an error color
 *   unchanged = type scale, radii, spacing, memory glyphs, marks ink recipe,
 *               the entire reading surface
 */
import { createTheme, type CSSVariablesResolver, type MantineColorsTuple } from '@mantine/core';

const ember: MantineColorsTuple = [
  '#f9ede4',
  '#f0d9c8',
  '#e6c2a6',
  '#dba983',
  '#d18f64',
  '#cd7a4d', // 5 — base accent (dark scheme)
  '#b8683f', // 6 — base accent (light scheme)
  '#9c5634',
  '#7f4529',
  '#63351f',
];

const sky: MantineColorsTuple = [
  '#e9f1fa',
  '#d3e2f4',
  '#b0cceb',
  '#8fb3e6', // 3 — dark-scheme tinted text (was #8db5e2)
  '#6ea3de',
  '#4f8fd9', // 5 — dark scheme
  '#3c76b8', // 6 — light scheme
  '#2f5f96',
  '#244a75',
  '#1a3757',
];

/** Light-scheme neutrals — cool-tinted (blue hour). gray[1] = app bg, white = panels. */
const gray: MantineColorsTuple = [
  '#f9fafd',
  '#edf0f6',
  '#e2e7f0',
  '#d3dae6',
  '#b9c2d2',
  '#939cb0',
  '#5d6880',
  '#3a465e',
  '#222c42',
  '#171f31',
];

/**
 * Dark-scheme neutrals — cool-tinted. Mapping to the theme-lab mockups:
 *   dark[0] text #e7ecf5 · dark[1] icons #c6d2e6 · dark[2] muted #8d97ad
 *   dark[3] faint #656f88 · dark[6] lifted panel #1d2638
 *   dark[7] panel #161d2e · dark[8] app bg #0e1320 · dark[9] wells/canvas #0a0e18
 */
const dark: MantineColorsTuple = [
  '#e7ecf5',
  '#c6d2e6',
  '#8d97ad',
  '#656f88',
  '#39435c',
  '#232c42',
  '#1d2638',
  '#161d2e',
  '#0e1320',
  '#0a0e18',
];

export const theme = createTheme({
  colors: { ember, sky, gray, dark },
  primaryColor: 'ember',
  primaryShade: { light: 6, dark: 5 },

  fontFamily: "'Noto Sans Georgian', -apple-system, 'Segoe UI', sans-serif",
  fontFamilyMonospace: 'ui-monospace, Menlo, Consolas, monospace',
  fontSizes: { xs: '11px', sm: '12.5px', md: '14px', lg: '16px', xl: '18px' },
  lineHeights: { xs: '1.4', sm: '1.45', md: '1.5', lg: '1.55', xl: '1.66' },
  headings: {
    fontFamily: "'Noto Sans Georgian', sans-serif",
    fontWeight: '600',
    sizes: {
      h1: { fontSize: '23px', lineHeight: '1.3' },
      h2: { fontSize: '19px', lineHeight: '1.35' },
      h3: { fontSize: '17px', lineHeight: '1.4' },
    },
  },

  radius: { xs: '4px', sm: '5px', md: '7px', lg: '10px', xl: '14px' },
  defaultRadius: 'md',
  spacing: { xs: '6px', sm: '10px', md: '14px', lg: '18px', xl: '24px' },

  // Quiet shadows + the two-layer float for hero surfaces. No glows on content.
  shadows: {
    xs: '0 1px 4px rgba(0,0,0,.2)',
    sm: '0 2px 8px rgba(0,0,0,.25)',
    md: '0 10px 30px rgba(0,0,0,.35)',
    lg: '0 14px 40px rgba(0,0,0,.42)',
    xl: '0 -14px 40px rgba(0,0,0,.5)', // bottom sheet
    // v2: hero float — pair with var(--nn-hero) surfaces
    float: '0 18px 44px rgba(0,0,0,.5), 0 2px 6px rgba(0,0,0,.3)',
  } as never,

  cursorType: 'pointer',

  components: {
    Button: { defaultProps: { radius: 'md' } },
    Drawer: { defaultProps: { radius: 'xl' } }, // bottom sheet: 14px top corners
    SegmentedControl: { defaultProps: { radius: 'md', size: 'xs' } },
    Badge: { defaultProps: { radius: 'sm' } },
    Paper: { defaultProps: { radius: 'lg' } },
    Slider: { defaultProps: { color: 'ember' } },
    Switch: { defaultProps: { color: 'ember' } },
  },

  other: {
    // Reading surfaces — UNCHANGED from v1 (see reading-surface-spec.md)
    transcriptFontSize: '17px',
    transcriptLineHeight: 1.66,
    transcriptEnFontSize: '13px',
    readerFontFamily: "'Noto Serif Georgian', serif",
    timeGutter: 'font-variant-numeric: tabular-nums',
    minHitBox: 40,
    glyphStates: ['unmet', 'shaky', 'solid'],
    speakerHue: 'oklch(0.72 0.08 H) dark / oklch(0.55 0.09 H) light — H = hash(name)',
    transliteration: 'national-2002 display · ISO-9984-style reversible key internal',
    errorTint: '#e0938c on rgba(224,100,92,.35)', // failed jobs / misses — NOT the accent

    // v2 role registry — which accent owns what (enforced by review, not by types)
    accentRoles: {
      ember: ['actions', 'active nav', 'due', 'judgment primary', 'goal ring'],
      sky: ['audio controls', 'forms mark (ვ‑)', 'sort/search affordances', 'links', 'info chips'],
      neither: ['reading-surface marks', 'memory glyphs', 'band headers', 'counts'],
    },
    // v2 gradient policy — the only legal gradient hosts
    gradientHosts: ['session card', 'list card', 'gloss sheet', 'primary button'],
  },
});

/** Semantic pairs — one var, both schemes. v2 adds wash/well/hero/halo/primary. */
export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {},
  dark: {
    '--nn-bg': '#0e1320',
    '--nn-panel': '#161d2e',
    '--nn-panel-2': '#1d2638', // lifted panel + playing line
    '--nn-text': '#e7ecf5',
    '--nn-text-strong': '#f3f6fb',
    '--nn-muted': '#8d97ad',
    '--nn-faint': '#656f88',
    '--nn-hairline': 'rgba(180,200,255,.08)',
    '--nn-hairline-lit': 'rgba(190,208,240,.22)', // hero top edges
    '--nn-accent': '#cd7a4d',
    '--nn-accent-text': '#e3b090',
    '--nn-on-accent': '#171009',
    '--nn-info': '#4f8fd9',
    '--nn-info-text': '#8fb3e6',
    '--nn-link': '#8fb3e6',
    '--nn-mark-ink': 'rgba(160,180,215,.6)',
    '--nn-mark-ink-chunk': 'rgba(160,180,215,.55)',
    '--nn-selection': 'color-mix(in oklab, #cd7a4d 24%, transparent)',
    // depth kit
    '--nn-wash': 'radial-gradient(140% 55% at 50% 0%, #141a29 0%, #0e1320 60%)',
    '--nn-well': '#0a0e18',
    '--nn-well-shadow': 'inset 0 2px 5px rgba(0,0,0,.5)',
    '--nn-hero': 'linear-gradient(180deg, #1c2436, #161d2e)',
    '--nn-halo-accent': '0 0 11px rgba(205,122,77,.5)', // text-shadow, active tab only
    // primary button (judgment "knew it" and equivalents)
    '--nn-primary-fill': 'linear-gradient(180deg, rgba(200,215,245,.17), rgba(200,215,245,.09))',
    '--nn-primary-border': 'rgba(190,208,240,.16)',
    '--nn-primary-edge': 'rgba(190,208,240,.32)',
    '--nn-primary-text': '#f3f6fb',
    '--nn-primary-shadow': '0 4px 12px rgba(0,0,0,.35)',
  },
  light: {
    '--nn-bg': '#edf0f6',
    '--nn-panel': '#ffffff',
    '--nn-panel-2': '#e2e7f0',
    '--nn-text': '#1a2130',
    '--nn-text-strong': '#10151f',
    '--nn-muted': '#5d6880',
    '--nn-faint': '#939cb0',
    '--nn-hairline': 'rgba(20,30,60,.09)',
    '--nn-hairline-lit': 'rgba(255,255,255,.95)',
    '--nn-accent': '#b8683f',
    '--nn-accent-text': '#9c5634',
    '--nn-on-accent': '#ffffff',
    '--nn-info': '#3c76b8',
    '--nn-info-text': '#3c76b8',
    '--nn-link': '#3c76b8',
    '--nn-mark-ink': 'rgba(93,104,128,.65)',
    '--nn-mark-ink-chunk': 'rgba(93,104,128,.6)',
    '--nn-selection': 'color-mix(in oklab, #b8683f 20%, transparent)',
    // depth kit — daylight inversion
    '--nn-wash': 'radial-gradient(140% 55% at 50% 0%, #f9fafd 0%, #edf0f6 60%)',
    '--nn-well': '#e2e7f0',
    '--nn-well-shadow': 'inset 0 2px 4px rgba(20,30,60,.09)',
    '--nn-hero': 'linear-gradient(180deg, #ffffff, #f6f8fc)',
    '--nn-halo-accent': '0 0 10px rgba(184,104,63,.3)',
    // primary inverts to solid — never a gray wash on white
    '--nn-primary-fill': 'linear-gradient(180deg, #33425f, #273349)',
    '--nn-primary-border': 'rgba(20,30,60,.2)',
    '--nn-primary-edge': 'rgba(255,255,255,.22)',
    '--nn-primary-text': '#f6f8fc',
    '--nn-primary-shadow': '0 5px 14px rgba(30,45,80,.28)',
  },
});

/**
 * Reading-surface rules — UNCHANGED from v1 (D2). The theme change stops at the
 * transcript's edge: marks, selection, playing line keep their v1 recipe, only the
 * ink vars above re-tint slightly with the neutrals.
 */
export const READING_SURFACE_CSS = `
.nn-reading {
  user-select: none; -webkit-user-select: none;
  -webkit-touch-callout: none; -webkit-tap-highlight-color: transparent;
  touch-action: pan-y pinch-zoom;
  font-size: var(--nn-transcript-size, 17px);
  line-height: 1.66;
}
.nn-reading .nn-line { content-visibility: auto; contain-intrinsic-size: auto 3.2rem; }
.nn-w { padding: .35em .04em; margin: 0 -.04em; }        /* ≥40px hit box */
.nn-w:active, .nn-w:hover { /* keep: touch-adjustment candidacy */ }
.nn-w--weak {
  text-decoration-line: underline; text-decoration-style: dotted;
  text-decoration-color: var(--nn-mark-ink);
  text-decoration-thickness: 1px; text-underline-offset: .2em;
  text-decoration-skip-ink: none;
}
.nn-chunk {
  text-decoration-line: underline; text-decoration-style: solid;
  text-decoration-color: var(--nn-mark-ink-chunk);
  text-decoration-thickness: 1px; text-underline-offset: .2em;
  text-decoration-skip-ink: none;
}
.nn-line--playing { background: var(--nn-panel-2); }
.nn-sel {
  background: var(--nn-selection); border-radius: 4px;
  padding: 1px 3px; margin: 0 -3px;
  -webkit-box-decoration-break: clone; box-decoration-break: clone;
}
[data-marks="strong"] .nn-w--weak { text-decoration-color: color-mix(in srgb, var(--nn-mark-ink) 100%, var(--nn-text) 40%); text-decoration-thickness: 1.5px; }
[data-marks="strong"] .nn-chunk  { text-decoration-color: color-mix(in srgb, var(--nn-mark-ink-chunk) 100%, var(--nn-text) 40%); text-decoration-thickness: 1.5px; }
[data-marks="off"] .nn-w--weak, [data-marks="off"] .nn-chunk { text-decoration-line: none; }
`;
